/**
 * Download link builders (drschoice/flow#615).
 *
 * S3 objects are private now: nothing links to the bucket directly. Files are
 * served by the API — a session-authenticated endpoint in the app, or a
 * server-minted tokenized `download_url` in print/email contexts where the
 * reader has no session (links inside emailed summary PDFs).
 */

// UploadedFile rows arrive as `id` (FileSerializer) or `file_id` (summary
// template state / SummaryFile join).
export function fileDownloadHref (file) {
    if (!file) return null
    if (file.download_url) return file.download_url
    const id = file.file_id || file.id
    return id ? `/api/extras/files/${id}/download` : null
}

// Oracle plan files arrive as `file_id` (quotes payload) or `id` (files API).
export function planFileDownloadHref (planFile) {
    if (!planFile) return null
    if (planFile.download_url) return planFile.download_url
    const id = planFile.file_id || planFile.id
    return id ? `/api/extras/plan-files/${id}/download` : null
}

// The label for a plan's carrier "Summary of Benefits" link. The extension
// comes from the last path segment only: taking the text after the URL's last
// dot printed a whole percent-encoded link, or a query string, as the
// "extension".
export function summaryOfBenefitsLabel (planName, url) {
    let pathname = ''
    try {
        pathname = new URL(url).pathname
    } catch (e) {
        // Not an absolute URL (an encoded one, say): no extension to show.
    }
    const match = /\.([a-z0-9]{1,5})$/i.exec(pathname.split('/').pop() || '')
    const extension = match ? `.${match[1].toLowerCase()}` : ''
    return `${planName} Summary of Benefits${extension}`
}

// summaryAttachments mixes external carrier PDFs (full_path), Oracle plan
// files, and uploaded summary files — route each to the right link.
export function attachmentHref (attachment) {
    if (!attachment) return null
    if (attachment.full_path) return attachment.full_path
    if (attachment.plan_id != null || attachment.file_category === 'plan') {
        return planFileDownloadHref(attachment)
    }
    return fileDownloadHref(attachment)
}

// Pre-#615 exclusion configs stored the public S3 URL of an uploaded file.
// Nothing fetches that URL any more, but it stays a recognized identity so
// saved configs keep excluding the same attachment.
export const LEGACY_S3_BASE = 'https://doctorschoice.s3.amazonaws.com'

function isPlanFile (attachment) {
    return attachment.plan_id != null || attachment.file_category === 'plan'
}

/**
 * Stable identity for the exclude-attachments config.
 *
 * External attachments keep their `full_path`, which is what existing configs
 * store. Uploaded files and Oracle plan files have no `full_path` — they used
 * to key as `undefined`, so ticking one struck through every one of them and
 * the backend could never match, emailing the file anyway. They now key by id,
 * namespaced so an uploaded-file id can't collide with a plan-file id.
 */
export function attachmentExcludeKey (attachment) {
    if (!attachment) return null
    if (attachment.full_path) return attachment.full_path
    const id = attachment.file_id || attachment.id
    if (!id) return null
    return isPlanFile(attachment) ? `plan-file:${id}` : `file:${id}`
}

/**
 * Which plan an upload should be attached to.
 *
 * Employer plans reach the card through /user-group-plans/all/, which merges
 * the UserGroupPlan dict over the GroupPlan dict — so `plan.id` there is the
 * UserGroupPlan id, and posting it as group_plan_id 404s against
 * `get_or_404(GroupPlan, ...)`. The real id survives the merge as
 * `group_plan_id`. Plain GroupPlan payloads (/group-plans/all/) have no such
 * key and their `id` is already the right one, hence the fallback.
 */
export function uploadFilesMetadata (plan, isGroupPlan) {
    if (!plan) return {}
    if (isGroupPlan) {
        return { group_plan_id: plan.group_plan_id || plan.id }
    }
    return { plan_id: plan.id }
}

export function isAttachmentExcludedBy (attachment, excluded) {
    if (!attachment || !excluded || !excluded.length) return false
    const key = attachmentExcludeKey(attachment)
    // A null key must never match the null entries an older config may hold.
    if (key && excluded.includes(key)) return true
    return !!attachment.file_s3_path &&
        excluded.includes(`${LEGACY_S3_BASE}/${attachment.file_s3_path}`)
}
